import { getEmailDomain } from 'utils/marketo/freeEmailSuppression';

/**
 * Domains we block outright on every lead-capture form as an in-browser layer
 * on top of the Marketo-side block. RevOps blocks these in Marketo first; this
 * list stops the submission before it ever reaches Marketo so the spam never
 * creates a lead, burns API volume, or triggers downstream automation.
 *
 * Unlike the free-email / edu suppression lists (which are per-campaign, keyed
 * by form ID), spam blocking is universal: a domain here is rejected on all
 * forms. Add domains as abuse patterns surface, remove them if a block turns
 * out to be a false positive.
 */
const SPAM_EMAIL_DOMAIN_LIST = [
  // Heavy spam wave reported 2026-07-23 (blocked in Marketo by RevOps).
  'outlook.jp',
] as const;

export const SPAM_EMAIL_DOMAINS: ReadonlySet<string> = new Set<string>(SPAM_EMAIL_DOMAIN_LIST);

/**
 * True when the email's domain is a blocked spam domain, or a subdomain of one
 * (e.g. `mail.outlook.jp`). Spam campaigns commonly rotate subdomains, so we
 * match the registrable domain and anything under it.
 */
export const isSpamEmail = (email: string | null | undefined): boolean => {
  const domain = getEmailDomain(email);
  if (!domain) {
    return false;
  }

  if (SPAM_EMAIL_DOMAINS.has(domain)) {
    return true;
  }

  return Array.from(SPAM_EMAIL_DOMAINS).some(blocked => domain.endsWith(`.${blocked}`));
};
